import { COLOR_TOKENS } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { SPACING } from '@backrs_ui/common-ui/src/designSystem/spacing/spacing';

import { Components } from 'react-markdown';

export type MarkdownContentSize = 'XLARGE' | 'LARGE' | 'SMALL';

export const getMarkdownComponents = (
  contentSize: MarkdownContentSize,
  navigate?: (path: string) => void,
): Components => ({
  table: ({ ...props }) => <table style={tableStyle} {...props} />,
  th: ({ ...props }) => <th style={tableHeaderStyle} {...props} />,
  td: ({ ...props }) => <td style={tableCellStyle} {...props} />,
  a: ({ href, ...props }) => {
    const isInternalLink = href?.startsWith('/');

    if (isInternalLink && navigate) {
      return (
        <a
          style={linkStyle}
          href={href}
          onClick={(e) => {
            e.preventDefault();
            if (href && navigate) {
              navigate(href);
            }
          }}
          {...props}
        />
      );
    }

    return <a style={linkStyle} href={href} target="_blank" rel="noopener noreferrer" {...props} />;
  },
  ul: ({ ...props }) => <ul style={listStyle(contentSize)} {...props} />,
  ol: ({ ...props }) => <ol style={listStyle(contentSize)} {...props} />,
  p: ({ ...props }) => <p style={paragraphStyle(contentSize)} {...props} />,
  li: ({ ...props }) => <li style={listItemStyle(contentSize)} {...props} />,
});

const tableStyle: React.CSSProperties = {
  borderCollapse: 'separate',
  borderSpacing: 0,
  width: '100%',
  borderTop: `2px solid ${COLOR_TOKENS['Colors/Text/text-primary']}`,
  borderBottom: `2px solid ${COLOR_TOKENS['Colors/Text/text-primary']}`,
  marginTop: `${SPACING['spacing-2xl']}`,
};

const tableHeaderStyle: React.CSSProperties = {
  border: 'none',
  padding: `${SPACING['spacing-xs']} ${SPACING['spacing-sm']}`,
  color: COLOR_TOKENS['Colors/Text/text-primary'],
  borderBottom: `2px solid ${COLOR_TOKENS['Colors/Text/text-primary']}`,
  textAlign: 'left',
};

const tableCellStyle: React.CSSProperties = {
  border: 'none',
  padding: `${SPACING['spacing-xs']} ${SPACING['spacing-sm']}`,
  whiteSpace: 'nowrap',
  width: 'fit-content',
};

const linkStyle: React.CSSProperties = {
  color: COLOR_TOKENS['Colors/Foreground/fg-brand-primary'],
  fontWeight: 'bold',
};

const fontSizeForSize = (contentSize: MarkdownContentSize): string =>
  contentSize === 'XLARGE' ? '20px' : contentSize === 'LARGE' ? '16px' : '14px';

const lineHeightForSize = (contentSize: MarkdownContentSize): string | undefined =>
  contentSize === 'XLARGE' ? '30px' : undefined;

const paragraphStyle = (contentSize: MarkdownContentSize): React.CSSProperties => ({
  fontSize: fontSizeForSize(contentSize),
  lineHeight: lineHeightForSize(contentSize),
  letterSpacing: 0,
});

const listItemStyle = (contentSize: MarkdownContentSize): React.CSSProperties => ({
  fontSize: fontSizeForSize(contentSize),
  lineHeight: lineHeightForSize(contentSize),
  marginBottom: '0.5em',
  letterSpacing: 0,
});

const listStyle = (contentSize: MarkdownContentSize): React.CSSProperties => ({
  paddingLeft: contentSize === 'SMALL' ? '1.5rem' : undefined,
  marginBottom: '0.5em',
  letterSpacing: 0,
});
